import "@/App.css";
import "@/lib/axiosInterceptor";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Home from "./pages/Home";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Settings from "./pages/Settings";
import UserSettings from "./pages/UserSettings";
import DecayCalculator from "./pages/DecayCalculator";
import AuditLogPage from "./pages/AuditLogPage";
import Trasferte from "./pages/Trasferte";
import Files from "./pages/Files";
import Planning from "./pages/Planning";
import Reminders from "./pages/Reminders";
import FileKeyRotation from "./pages/FileKeyRotation";
import DbKeyRotation from "./pages/DbKeyRotation";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-transparent">
        <div className="w-6 h-6 border-2 border-indigo-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return children;
}

function LanguageWrapper({ children }) {
  const { user } = useAuth();
  // Italian is the default: everyone who uses Core speaks it, and the login
  // screen is shown before there is a user whose preference could be read.
  return (
    <LanguageProvider initialLang={user?.language || "it"}>
      {children}
    </LanguageProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageWrapper>
        <BrowserRouter>
          <Routes>
            {/* Public routes — no Navbar */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected routes — shared Layout with Navbar */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/decay" element={<DecayCalculator />} />
              <Route path="/profile" element={<UserSettings />} />
              {/* Administration. Every endpoint this page calls is admin-only,
                  so without the guard a non-administrator reached the whole
                  console and read nothing but errors. Personal settings live
                  on /profile and stay open to everyone. */}
              <Route
                path="/settings"
                element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>}
              />
              {/* The two encryption keys, each with its own page. They are
                  procedures with steps and a state that outlives a visit, not
                  forms to fill in - and they are dangerous in different ways,
                  which is easier to say on two pages than on one. */}
              <Route
                path="/settings/file-key"
                element={<ProtectedRoute adminOnly><FileKeyRotation /></ProtectedRoute>}
              />
              <Route
                path="/settings/db-key"
                element={<ProtectedRoute adminOnly><DbKeyRotation /></ProtectedRoute>}
              />
              {/* The activity log records every account's actions, names and
                  addresses included, and its endpoints are admin-only. */}
              <Route
                path="/audit"
                element={<ProtectedRoute adminOnly><AuditLogPage /></ProtectedRoute>}
              />
              <Route path="/trasferte" element={<Trasferte />} />
              {/* Files lists every file in the system, so the route is guarded
                  as well as hidden from the home page. */}
              <Route
                path="/files"
                element={<ProtectedRoute adminOnly><Files /></ProtectedRoute>}
              />
              {/* Planning reads the workbook the company keeps on SharePoint
                  and never writes to it. Open to everyone, since the planning
                  is the one document the whole company is meant to read. */}
              <Route path="/planning" element={<Planning />} />
              {/* Every account's own reminders. Open to every role: the
                  server only ever answers with the caller's own. */}
              <Route path="/promemoria" element={<Reminders />} />
              {/* Accounting was removed; an old link lands on the home page
                  rather than a blank screen. */}
              <Route path="/accounting" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </LanguageWrapper>
    </AuthProvider>
  );
}

export default App;
